import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Mic, Square, Play, Pause, Trash2, RotateCcw, Check, Plus, Music, X } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { useMeditations } from '@/hooks/useMeditations';
import { useMeditationPlayerStore } from '@/store/meditationPlayerStore';
import { useBodyClass } from '@/hooks/useBodyClass';
import { BreathingOrb } from '@/components/BreathingOrb';
import { SelfSupportLesson } from '@/components/SelfSupportLesson';
import {
  listRecordings, saveRecording, deleteRecording, pickRecordingMime, canRecord, type SelfRecording,
} from '@/lib/selfSupportStore';

/**
 * Практика самоподдержки (13.10.2026, идея Славы: «самомантра — надиктовать своим
 * голосом и проговаривать под музыку»).
 *
 * Человек пишет несколько фраз поддержки, записывает их своим голосом и слушает по кругу
 * под музыку. Подаётся именно как САМОПОДДЕРЖКА, а не «программирование подсознания»:
 * за вторым нет доказательств, и обещать его нельзя. Честная основа скромнее —
 * самоаффирмация и обращение к себе на «ты» помогают регулировать эмоции.
 *
 * Запись и текст хранятся ТОЛЬКО на телефоне (selfSupportStore.ts), на сервер не уходит
 * ничего. Музыка — из каталога Релакса.
 *
 * Пока бесплатно; Слава включит в стоимость нового тарифа, когда он появится.
 */

// ⚠️ Черновики фраз — Славе на правку как психологу. Правила: коротко, в настоящем
// времени, на «ты», без «не» и без обещаний результата; без родовых окончаний.
const THEMES: { id: string; emoji: string; title: string; text: (name: string) => string }[] = [
  { id: 'calm', emoji: '🌬', title: 'Когда тревожно', text: (n) => `${n}, ты в безопасности. Сейчас достаточно просто дышать. Тревога поднимается и спадает — так было каждый раз. Ты можешь опереться на себя.` },
  { id: 'sleep', emoji: '🌙', title: 'Перед сном', text: (n) => `${n}, день закончен. Всё, что нужно было сегодня, уже сделано. Тело может отпустить. Мысли подождут до утра.` },
  { id: 'craving', emoji: '🍃', title: 'Когда тянет к еде', text: (n) => `${n}, это желание — волна: она поднимется и спадёт. Ты можешь подождать десять минут. Выбираешь ты, а не тяга.` },
  { id: 'strength', emoji: '🌱', title: 'Опора и уверенность', text: (n) => `${n}, ты делаешь достаточно. У тебя есть право идти своим темпом. Один шаг сегодня — это уже движение.` },
];

const MAX_SECONDS = 60;
const GAP_SECONDS = 7; // пауза между повторами — время, чтобы фраза отзвучала внутри
const DURATIONS = [3, 5, 10];
const MUSIC_VOLUME = 0.22;

type Step = 'list' | 'text' | 'record' | 'play';

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export default function SelfSupport() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const name = profile?.first_name?.trim() || 'Друг';

  const [step, setStep] = useState<Step>('list');
  const [items, setItems] = useState<SelfRecording[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [current, setCurrent] = useState<SelfRecording | null>(null);

  // На шагах записи и прослушивания нижняя панель мешает кнопкам
  useBodyClass('modal-open', step !== 'list');

  useEffect(() => {
    listRecordings().then((r) => { setItems(r); setLoaded(true); });
  }, []);

  function startNew(theme?: (typeof THEMES)[number]) {
    haptic('light');
    setText(theme ? theme.text(name) : '');
    setTitle(theme ? theme.title : 'Мои слова');
    setStep('text');
  }

  async function remove(id: string) {
    haptic('medium');
    await deleteRecording(id);
    setItems(await listRecordings());
  }

  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto min-h-screen">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => (step === 'list' ? nav(-1) : setStep('list'))}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center shrink-0"
          aria-label="Назад"
        >
          <ChevronLeft size={18} />
        </button>
        <div>
          <div className="font-display text-lg font-bold leading-tight">Практика самоподдержки</div>
          <div className="text-xs text-ink-dim">Твои слова, твой голос, музыка</div>
        </div>
      </div>

      {step === 'list' && (
        <div className="space-y-4">
          {/* Урок от Славы — появится сам, как только файл будет загружен */}
          <SelfSupportLesson />
          <div className="card p-4">
            <p className="text-sm leading-relaxed">
              Напиши несколько фраз, которые тебя поддерживают, запиши их своим голосом — и слушай
              под музыку, когда нужна опора.
            </p>
            <p className="text-xs text-ink-dim mt-2 leading-relaxed">
              Обращение к себе по имени и на «ты» помогает успокоиться и увидеть ситуацию со стороны.
              Это способ поддержать себя, а не замена помощи специалиста.
            </p>
          </div>

          {!canRecord() && (
            <div className="rounded-2xl border border-warn/40 bg-warn/5 p-4 text-sm">
              На этом устройстве запись голоса недоступна. Открой приложение в Telegram на телефоне.
            </div>
          )}

          {loaded && items.length > 0 && (
            <div>
              <div className="label mb-2">Мои записи</div>
              <div className="space-y-2">
                {items.map((r) => (
                  <div key={r.id} className="card p-3 flex items-center gap-3">
                    <button
                      onClick={() => { haptic('light'); setCurrent(r); setStep('play'); }}
                      className="w-11 h-11 rounded-full bg-emerald-glow text-bg flex items-center justify-center shrink-0 active:scale-95 transition-transform"
                      aria-label={`Слушать: ${r.title}`}
                    >
                      <Play size={17} className="fill-current ml-0.5" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.title}</div>
                      <div className="text-[11px] text-ink-dim truncate">{fmt(r.seconds)} · {r.text}</div>
                    </div>
                    <button onClick={() => remove(r.id)} className="w-9 h-9 rounded-lg text-ink-dim hover:text-danger flex items-center justify-center shrink-0" aria-label="Удалить запись">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canRecord() && (
            <div>
              <div className="label mb-2">Новая запись</div>
              <div className="grid grid-cols-2 gap-2">
                {THEMES.map((t) => (
                  <button key={t.id} onClick={() => startNew(t)} className="card p-3 text-left active:scale-[0.98] transition-transform">
                    <div className="text-xl mb-1">{t.emoji}</div>
                    <div className="text-sm font-medium leading-tight">{t.title}</div>
                    <div className="text-[11px] text-ink-dim mt-0.5">готовые фразы, можно править</div>
                  </button>
                ))}
              </div>
              <button onClick={() => startNew()} className="w-full mt-2 py-3 rounded-xl border border-dashed border-line text-sm text-ink-muted flex items-center justify-center gap-2 active:scale-[0.99] transition-transform">
                <Plus size={16} /> Написать свои слова
              </button>
            </div>
          )}

          <p className="text-[11px] text-ink-dim leading-relaxed text-center px-2">
            🔒 Записи и тексты хранятся только на этом телефоне и никуда не отправляются.
            Если переустановить Telegram или сменить телефон, они не сохранятся.
          </p>
        </div>
      )}

      {step === 'text' && (
        <div className="space-y-4">
          <div>
            <label className="label mb-2 block">Название</label>
            <input className="input text-sm" maxLength={40} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label className="label mb-2 block">Твои слова</label>
            <textarea
              className="input text-sm leading-relaxed min-h-[150px]"
              maxLength={400}
              placeholder={`${name}, …`}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="text-[11px] text-ink-dim mt-1.5 leading-relaxed">
              Три-четыре коротких фразы. В настоящем времени, на «ты» или по имени, про то, что ты
              можешь сделать сейчас. Так, как сказал бы близкий человек.
            </div>
          </div>
          <button
            onClick={() => { haptic('medium'); setStep('record'); }}
            disabled={text.trim().length < 10}
            className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform"
          >
            <Mic size={16} /> Дальше — записать голосом
          </button>
        </div>
      )}

      {step === 'record' && (
        <Recorder
          text={text}
          onCancel={() => setStep('text')}
          onSaved={async (blob, mime, seconds) => {
            const rec: SelfRecording = {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              title: title.trim() || 'Мои слова', text: text.trim(), blob, mime, seconds, createdAt: Date.now(),
            };
            await saveRecording(rec);
            setItems(await listRecordings());
            setCurrent(rec);
            setStep('play');
          }}
        />
      )}

      {step === 'play' && current && <Player rec={current} onClose={() => setStep('list')} />}
    </div>
  );
}

function Recorder({ text, onCancel, onSaved }: { text: string; onCancel: () => void; onSaved: (blob: Blob, mime: string, seconds: number) => Promise<void> }) {
  const { haptic } = useTelegram();
  const [state, setState] = useState<'idle' | 'rec' | 'done'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const result = useRef<{ blob: Blob; mime: string } | null>(null);
  const timer = useRef<number | null>(null);
  const startedAt = useRef(0);

  function cleanup() {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  }
  useEffect(() => () => { cleanup(); if (previewUrl) URL.revokeObjectURL(previewUrl); }, []);

  async function start() {
    setError(null);
    haptic('medium');
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      setError('Нет доступа к микрофону. Разреши его для Telegram в настройках телефона и попробуй ещё раз.');
      return;
    }
    const mime = pickRecordingMime();
    try {
      rec.current = mime ? new MediaRecorder(stream.current, { mimeType: mime }) : new MediaRecorder(stream.current);
    } catch {
      setError('Этот телефон не умеет записывать звук в приложении.');
      cleanup();
      return;
    }
    chunks.current = [];
    rec.current.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    rec.current.onstop = () => {
      const type = rec.current?.mimeType || mime || 'audio/webm';
      const blob = new Blob(chunks.current, { type });
      result.current = { blob, mime: type };
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setState('done');
      cleanup();
    };
    rec.current.start();
    startedAt.current = Date.now();
    setSeconds(0);
    setState('rec');
    timer.current = window.setInterval(() => {
      const s = (Date.now() - startedAt.current) / 1000;
      setSeconds(s);
      if (s >= MAX_SECONDS) stop();
    }, 200);
  }

  function stop() {
    haptic('light');
    if (timer.current) window.clearInterval(timer.current);
    setSeconds((Date.now() - startedAt.current) / 1000);
    try { rec.current?.stop(); } catch { /* уже остановлен */ }
  }

  async function save() {
    if (!result.current || saving) return;
    setSaving(true);
    haptic('success');
    try {
      await onSaved(result.current.blob, result.current.mime, Math.max(1, Math.round(seconds)));
    } catch {
      setError('Не получилось сохранить запись на телефоне. Возможно, не хватает места.');
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="label mb-2">Читай вслух, спокойно и не спеша</div>
        <p className="text-base leading-relaxed whitespace-pre-line">{text}</p>
      </div>

      {error && <div className="rounded-xl border border-danger/40 bg-danger/5 p-3 text-sm text-danger">{error}</div>}

      <div className="flex flex-col items-center py-2">
        {state !== 'done' ? (
          <>
            <button
              onClick={state === 'rec' ? stop : start}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                state === 'rec' ? 'bg-danger text-white animate-pulse' : 'bg-emerald-glow text-bg'
              }`}
              aria-label={state === 'rec' ? 'Остановить запись' : 'Начать запись'}
            >
              {state === 'rec' ? <Square size={30} className="fill-current" /> : <Mic size={34} />}
            </button>
            <div className="mt-3 text-sm tabular-nums">{state === 'rec' ? `${fmt(seconds)} / ${fmt(MAX_SECONDS)}` : 'Нажми и говори'}</div>
            <div className="text-[11px] text-ink-dim mt-1">До минуты. Запись останется только на этом телефоне.</div>
          </>
        ) : (
          <>
            {previewUrl && <audio src={previewUrl} controls className="w-full" />}
            <div className="text-[11px] text-ink-dim mt-2">Послушай. Не понравилось — перезапиши, это нормально.</div>
          </>
        )}
      </div>

      {state === 'done' ? (
        <div className="flex gap-2">
          <button onClick={() => { setState('idle'); setSeconds(0); }} className="flex-1 py-3 rounded-xl bg-bg-card border border-line text-sm flex items-center justify-center gap-2">
            <RotateCcw size={15} /> Перезаписать
          </button>
          <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-xl bg-emerald-glow text-bg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            <Check size={16} /> Сохранить
          </button>
        </div>
      ) : (
        <button onClick={onCancel} className="w-full py-2.5 text-sm text-ink-dim">Вернуться к тексту</button>
      )}
    </div>
  );
}

function Player({ rec, onClose }: { rec: SelfRecording; onClose: () => void }) {
  const { haptic } = useTelegram();
  const { meditations } = useMeditations();
  const [minutes, setMinutes] = useState(5);
  const [musicUrl, setMusicUrl] = useState<string | null | undefined>(undefined); // undefined — ещё не выбирали
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [left, setLeft] = useState(0);
  const [beat, setBeat] = useState(0);
  const voice = useRef<HTMLAudioElement | null>(null);
  const music = useRef<HTMLAudioElement | null>(null);
  const voiceUrl = useRef<string | null>(null);
  const gap = useRef<number | null>(null);
  const tick = useRef<number | null>(null);
  const live = useRef({ paused: false, running: false });

  // По умолчанию — та же музыка, что играет под дыхание
  useEffect(() => {
    if (musicUrl !== undefined || !meditations.length) return;
    const def = meditations.find((m: any) => m.for_breathing) ?? meditations[0];
    setMusicUrl(def?.audio_url ?? null);
  }, [meditations]);

  useEffect(() => {
    const t = window.setInterval(() => setBeat((b) => b + 1), 5000);
    return () => window.clearInterval(t);
  }, []);

  function clearTimers() {
    if (gap.current) window.clearTimeout(gap.current);
    if (tick.current) window.clearInterval(tick.current);
    gap.current = null;
    tick.current = null;
  }

  function teardown() {
    clearTimers();
    live.current = { paused: false, running: false };
    voice.current?.pause();
    music.current?.pause();
    if (voiceUrl.current) URL.revokeObjectURL(voiceUrl.current);
    voiceUrl.current = null;
  }
  useEffect(() => teardown, []);

  // Запуск — из обработчика нажатия: iOS разрешает звук только так
  function start() {
    haptic('medium');
    try { useMeditationPlayerStore.getState().pause(); } catch { /* плеер не поднят */ }
    voiceUrl.current = URL.createObjectURL(rec.blob);
    const v = new Audio(voiceUrl.current);
    voice.current = v;
    v.onended = () => {
      if (!live.current.running) return;
      gap.current = window.setTimeout(() => {
        if (live.current.running && !live.current.paused) v.play().catch(() => {});
      }, GAP_SECONDS * 1000);
    };
    if (musicUrl) {
      const m = new Audio(musicUrl);
      m.loop = true;
      m.volume = MUSIC_VOLUME;
      music.current = m;
      m.play().catch(() => {});
    }
    // Голос вступает через пару секунд, когда музыка уже звучит
    live.current = { paused: false, running: true };
    gap.current = window.setTimeout(() => v.play().catch(() => {}), 2500);
    setLeft(minutes * 60);
    setRunning(true);
    setPaused(false);
    tick.current = window.setInterval(() => {
      if (live.current.paused) return;
      setLeft((s) => {
        if (s <= 1) { finish(); return 0; }
        return s - 1;
      });
    }, 1000);
  }

  function finish() {
    clearTimers();
    live.current.running = false;
    // Дать голосу договорить фразу, музыку увести плавно
    const m = music.current;
    if (m) {
      let i = 0;
      const from = m.volume;
      const f = window.setInterval(() => {
        i++;
        m.volume = Math.max(0, from * (1 - i / 40));
        if (i >= 40) { window.clearInterval(f); m.pause(); }
      }, 75);
    }
    haptic('success');
    setRunning(false);
  }

  function togglePause() {
    haptic('light');
    const next = !paused;
    live.current.paused = next;
    setPaused(next);
    if (next) {
      voice.current?.pause();
      music.current?.pause();
      if (gap.current) window.clearTimeout(gap.current);
    } else {
      music.current?.play().catch(() => {});
      voice.current?.play().catch(() => {});
    }
  }

  function stop() {
    haptic('medium');
    teardown();
    setRunning(false);
    onClose();
  }

  const musical = meditations.filter((m: any) => m.audio_url);

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center">
        <BreathingOrb size={230} color="#10E58E" phase={beat % 2 === 0 ? 'inhale' : 'exhale'} duration={5} phaseKey={beat} paused={running && paused} />
        <div className="font-display font-bold text-base -mt-1">{rec.title}</div>
        {running ? (
          <div className="text-sm text-ink-muted tabular-nums mt-1">осталось {fmt(left)}</div>
        ) : (
          <div className="text-xs text-ink-dim mt-1 text-center px-6 line-clamp-2">{rec.text}</div>
        )}
      </div>

      {!running ? (
        <>
          <div>
            <div className="label mb-2">Сколько слушать</div>
            <div className="grid grid-cols-3 gap-2">
              {DURATIONS.map((d) => (
                <button key={d} onClick={() => { setMinutes(d); haptic('light'); }}
                  className={`py-2.5 rounded-xl border text-sm ${minutes === d ? 'bg-emerald-glow/15 border-emerald-glow/50' : 'bg-bg-card border-line text-ink-muted'}`}>
                  {d} мин
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label mb-2 flex items-center gap-1.5"><Music size={12} /> Музыка</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => { setMusicUrl(null); haptic('light'); }}
                className={`px-3 py-1.5 rounded-full border text-xs ${musicUrl === null ? 'bg-emerald-glow/15 border-emerald-glow/50' : 'bg-bg-card border-line text-ink-muted'}`}>
                Без музыки
              </button>
              {musical.map((m: any) => (
                <button key={m.id} onClick={() => { setMusicUrl(m.audio_url); haptic('light'); }}
                  className={`px-3 py-1.5 rounded-full border text-xs ${musicUrl === m.audio_url ? 'bg-emerald-glow/15 border-emerald-glow/50' : 'bg-bg-card border-line text-ink-muted'}`}>
                  {m.title}
                </button>
              ))}
            </div>
          </div>
          <button onClick={start} className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.99] transition-transform">
            <Play size={16} className="fill-current" /> Слушать
          </button>
          <p className="text-[11px] text-ink-dim text-center leading-relaxed">
            Твой голос будет повторяться с паузами. Лучше в наушниках и с закрытыми глазами.
          </p>
        </>
      ) : (
        <div className="flex gap-2">
          <button onClick={togglePause} className="flex-1 py-3.5 rounded-xl bg-bg-card border border-line text-sm flex items-center justify-center gap-2">
            {paused ? <Play size={15} /> : <Pause size={15} />} {paused ? 'Продолжить' : 'Пауза'}
          </button>
          <button onClick={stop} className="flex-1 py-3.5 rounded-xl bg-bg-card border border-line text-sm text-ink-muted flex items-center justify-center gap-2">
            <X size={15} /> Завершить
          </button>
        </div>
      )}
    </div>
  );
}
